import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from './../../contexts/UserContext';
import { getUnitsCount, getAvailableLocations, getUnitStateListener, getWarnings } from './../../hooks/unitDataHooks.js';
import { db } from '../../firebase.js';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';

import './HomePage.css';
import SignupLinkPanels from './SignupLinkPanels.jsx';
import { getAccessibleCompanyIds, getAccessibleWasteReceiverIds } from '../../hooks/unitDataHooks.js';

const APP_OWNER_TYPES = {
    company: 'company',
    wasteReceiver: 'waste-receiver',
};

const COMPANY_APP_RECORDS_COLLECTION = 'company_app_records';
const WASTE_RECEIVER_APP_RECORDS_COLLECTIONS = [
    'waste_receiver_app_records',
    'waster_receiver_app_records',
];
const APP_RECORDS_SUBCOLLECTION = 'dangerous-waste-records';

const normalizeEvalRequestsMap = (rawValue) => {
    if (!rawValue) return {};

    if (Array.isArray(rawValue)) {
        const converted = {};
        rawValue.forEach((request, index) => {
            const requestId = String(request?.id || `request_${index}`);
            converted[requestId] = request;
        });
        return converted;
    }

    if (typeof rawValue === 'object') {
        return rawValue;
    }

    return {};
};

const hasUnresolvedEvalRequests = (record) => {
    const evalRequests = normalizeEvalRequestsMap(record?.user_eval_requests);
    return Object.values(evalRequests).some((request) => !Boolean(request?.resolved));
};

const getCompanyName = (companyId, data = {}) => (
    data.name || data.display_name || data.label || companyId
);

const STATUS_RECENCY_WINDOW_MS = 60 * 60 * 1000;

const toTimestampMs = (value) => {
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) return numericValue;
    }
    if (value && typeof value.toMillis === 'function') return value.toMillis();
    if (value && typeof value.toDate === 'function') {
        const dateValue = value.toDate();
        return dateValue instanceof Date ? dateValue.getTime() : Number.NaN;
    }
    return Number.NaN;
};

const getSeverityForState = (state) => {
    switch (state) {
        case 'Error':
            return 3;
        case 'Warning':
            return 2;
        case 'Ready':
            return 1;
        default:
            return 0;
    }
};

const getColorForState = (state) => {
    switch (state) {
        case 'Ready':
            return 'var(--success-500)';
        case 'Error':
            return 'var(--error-500)';
        case 'Warning':
            return 'var(--warning-500)';
        default:
            return 'var(--gray-500)';
    }
};

const HomePage = () => {
    const [locations, setLocations] = useState([]);
    const [appCards, setAppCards] = useState([]);
    const [reviewBacklog, setReviewBacklog] = useState({ status: 'idle', total: null, sources: [] });
    const [statesForLocation, setStatesForLocation] = useState({});
    const { userData } = useUser();
    
    const navigate = useNavigate();

    const navigateToLocation = useCallback((locationId) => {
        navigate(`/locations/${locationId}`)
    }, [navigate])

    const navigateToAppCard = useCallback((appCard) => {
        navigate(`/app-records/${appCard.ownerType}/${appCard.ownerId}/dangerous-waste/dashboard`);
    }, [navigate]);

    const appRecordPathHasRecords = async (collectionName, ownerId) => {
        const recordsSnap = await getDocs(query(
            collection(db, collectionName, ownerId, APP_RECORDS_SUBCOLLECTION),
            limit(1)
        ));
        return !recordsSnap.empty;
    };

    const getExistingWasteReceiverAppRecordCollection = async (wasteReceiverId) => {
        for (const collectionName of WASTE_RECEIVER_APP_RECORDS_COLLECTIONS) {
            if (await appRecordPathHasRecords(collectionName, wasteReceiverId)) {
                return collectionName;
            }
        }

        return null;
    };

    const loadWasteReceiverAppCard = async (wasteReceiverId) => {
        const appRecordCollection = await getExistingWasteReceiverAppRecordCollection(wasteReceiverId);
        if (!appRecordCollection) return null;

        const wasteReceiverSnap = await getDoc(doc(db, 'waste_receivers', wasteReceiverId));
        const wasteReceiverData = wasteReceiverSnap.exists() ? wasteReceiverSnap.data() : {};
        const name = wasteReceiverData?.name || wasteReceiverId;
        return {
            ownerType: APP_OWNER_TYPES.wasteReceiver,
            ownerId: wasteReceiverId,
            title: `${name} - App scanninger`,
            sourceLabel: `${name} - App scanninger`,
            testCompany: false,
        };
    };

    const loadCompanyAppCard = async (companyId) => {
        const hasAppRecords = await appRecordPathHasRecords(COMPANY_APP_RECORDS_COLLECTION, companyId);
        if (!hasAppRecords) return null;

        const companySnap = await getDoc(doc(db, 'companies', companyId));
        const companyData = companySnap.exists() ? companySnap.data() : {};
        return {
            ownerType: APP_OWNER_TYPES.company,
            ownerId: companyId,
            title: `${getCompanyName(companyId, companyData)} - App scanninger`,
            sourceLabel: `${getCompanyName(companyId, companyData)} - App scanninger`,
            testCompany: companyData.test_company === true,
        };
    };

    const getReviewCount = async (scopes, { deduplicateById = false } = {}) => {
        const snapshots = await Promise.all(scopes.map(async (scope) => {
            const constraints = [];
            if (Number.isFinite(scope.startDate)) {
                constraints.push(where('timestamp', '>=', scope.startDate));
            }
            if (Number.isFinite(scope.endDate)) {
                constraints.push(where('timestamp', '<=', scope.endDate));
            }
            constraints.push(orderBy('timestamp', 'desc'));

            return getDocs(query(
                collection(db, scope.collectionPath),
                ...constraints,
            ));
        }));

        if (deduplicateById) {
            const recordsById = new Map();
            snapshots.forEach((snapshot) => {
                snapshot.docs.forEach((_doc) => {
                    if (recordsById.has(_doc.id)) return;
                    recordsById.set(_doc.id, _doc.data());
                });
            });

            return [...recordsById.values()]
                .filter((record) => record?.is_test !== true)
                .filter((record) => hasUnresolvedEvalRequests(record))
                .length;
        }

        let unresolvedCount = 0;
        snapshots.forEach((snapshot) => {
            snapshot.docs.forEach((_doc) => {
                const record = _doc.data();
                if (record?.is_test === true) return;
                if (hasUnresolvedEvalRequests(record)) {
                    unresolvedCount += 1;
                }
            });
        });

        return unresolvedCount;
    };

    const getMachineReviewSources = (nextLocations, testCompanyIds) => nextLocations.flatMap((location) => {
        const locationName = location.name || location.id;
        if (Array.isArray(location.display_units)) {
            return location.display_units.map((displayUnit, index) => {
                const displayUnitName = displayUnit.display_name || displayUnit.description || `Enhed ${index + 1}`;
                const ranges = Array.isArray(displayUnit.unit_ranges) ? displayUnit.unit_ranges : [];
                const scopes = ranges
                    .filter((range) => range?.unit_id)
                    .map((range) => ({
                        collectionPath: `units/${range.unit_id}/${APP_RECORDS_SUBCOLLECTION}`,
                        startDate: Number.isFinite(range.start_date) ? range.start_date : 0,
                        endDate: range.current === true || !Number.isFinite(range.end_date)
                            ? Date.now()
                            : range.end_date,
                    }));
                return {
                    key: `machine-${location.id}-${index}`,
                    label: `${locationName} - ${displayUnitName}`,
                    scopes,
                    testCompany: testCompanyIds.has(location.company),
                };
            });
        }

        return (Array.isArray(location.units) ? location.units : []).map((unitId) => ({
            key: `machine-${location.id}-${unitId}`,
            label: `${locationName} - ${unitId}`,
            scopes: [{
                collectionPath: `units/${unitId}/${APP_RECORDS_SUBCOLLECTION}`,
                startDate: 0,
                endDate: Date.now(),
            }],
            testCompany: testCompanyIds.has(location.company),
        }));
    });

    const loadReviewBacklog = async (nextAppCards, nextLocations) => {
        setReviewBacklog({ status: 'loading', total: null, sources: [] });
        try {
            const companyIds = [...new Set(nextLocations.map((location) => location.company).filter(Boolean))];
            const companySnapshots = await Promise.all(
                companyIds.map((companyId) => getDoc(doc(db, 'companies', companyId))),
            );
            const testCompanyIds = new Set(
                companySnapshots
                    .filter((snapshot) => snapshot.exists() && snapshot.data()?.test_company === true)
                    .map((snapshot) => snapshot.id),
            );
            const sources = [
                ...nextAppCards
                    .filter((appCard) => !appCard.testCompany)
                    .map((appCard) => ({
                        key: `app-${appCard.ownerType}-${appCard.ownerId}`,
                        ownerType: appCard.ownerType,
                        label: appCard.sourceLabel,
                        scopes: appCard.ownerType === APP_OWNER_TYPES.company
                            ? [{ collectionPath: `${COMPANY_APP_RECORDS_COLLECTION}/${appCard.ownerId}/${APP_RECORDS_SUBCOLLECTION}` }]
                            : WASTE_RECEIVER_APP_RECORDS_COLLECTIONS.map((collectionName) => ({
                                collectionPath: `${collectionName}/${appCard.ownerId}/${APP_RECORDS_SUBCOLLECTION}`,
                            })),
                    })),
                ...getMachineReviewSources(nextLocations, testCompanyIds).filter((source) => !source.testCompany),
            ];
            const uniqueSources = [...new Map(sources.map((source) => [source.key, source])).values()];
            const countResults = await Promise.allSettled(uniqueSources.map(async (source) => ({
                ...source,
                count: await getReviewCount(source.scopes, {
                    deduplicateById: source.ownerType === APP_OWNER_TYPES.wasteReceiver,
                }),
            })));
            const countedSources = countResults
                .filter((result) => result.status === 'fulfilled')
                .map((result) => result.value);
            const activeSources = countedSources
                .filter((source) => source.count > 0)
                .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
            const hasCountError = countResults.some((result) => result.status === 'rejected');
            setReviewBacklog({
                status: hasCountError ? 'error' : 'ready',
                total: hasCountError ? null : activeSources.reduce((total, source) => total + source.count, 0),
                sources: activeSources,
            });
        } catch (error) {
            console.error('Error loading unresolved review backlog:', error);
            setReviewBacklog({ status: 'error', total: null, sources: [] });
        }
    };

    const loadAppCards = async (nextUserData) => {
        // An admin sees every app owner that has scans, not only the ones they are assigned.
        const wasteReceiverIds = await getAccessibleWasteReceiverIds(nextUserData);
        const wasteReceiverCards = await Promise.all(
            wasteReceiverIds.map(loadWasteReceiverAppCard)
        );

        const accessibleCompanyIds = await getAccessibleCompanyIds(nextUserData);
        const companyCards = await Promise.all(
            accessibleCompanyIds.map(loadCompanyAppCard)
        );

        return [...wasteReceiverCards, ...companyCards].filter(Boolean);
    };

    const getColorForLocation = (locationId) => (
        getColorForState(statesForLocation[locationId]?.worstState)
    );

    useEffect(() => {
        var unsubscribeCallbacks = []
        if (locations) {
            for (let i = 0; i < locations.length; i++) {
                const location = locations[i];
                var unitIds = []
                const now = Date.now();
                if (location.display_units) {
                    for (let j = 0; j < location.display_units.length; j++) {
                        const display_unit = location.display_units[j];
                        const unit_ranges = display_unit.unit_ranges
                        if (unit_ranges) {
                            const activeUnitIds = unit_ranges
                                .filter((range) => {
                                    if (!range?.unit_id) return false;
                                    if (range.current === true) return true;
                                    if (range.start_date === undefined || range.start_date === null) return false;
                                    if (range.end_date === undefined || range.end_date === null) return false;
                                    return now >= range.start_date && now <= range.end_date;
                                })
                                .map((range) => range.unit_id);
                            unitIds = [...unitIds, ...activeUnitIds];
                        }
                    }
                    unitIds = [...new Set(unitIds)];
                } else {
                    unitIds = locations[i].units;
                }
                
                for (let j = 0; j < unitIds.length; j++) {
                    const unitId = unitIds[j];
                    const unsubscribeFirestore = getUnitStateListener(unitId, {
                        next: (log) => {
                            setStatesForLocation((prevStates) => {          
                                const locationId = locations[i].id;
                                const currentLocationState = prevStates[locationId] || { unitStates: {} };
                                const nextUnitStates = {
                                    ...(currentLocationState.unitStates || {}),
                                    [unitId]: {
                                        timestampMs: toTimestampMs(log.timestamp),
                                        state: log.state,
                                    },
                                };
                                const recentUnitStates = Object.values(nextUnitStates).filter(
                                    (unitState) =>
                                        Number.isFinite(unitState.timestampMs) &&
                                        unitState.timestampMs >= Date.now() - STATUS_RECENCY_WINDOW_MS,
                                );
                                const finiteTimestamps = Object.values(nextUnitStates)
                                    .map((unitState) => unitState.timestampMs)
                                    .filter(Number.isFinite);
                                const newestTimestampMs = finiteTimestamps.length > 0
                                    ? Math.max(...finiteTimestamps)
                                    : Number.NaN;

                                const worstRecentState = recentUnitStates.reduce((worstState, unitState) => {
                                    return getSeverityForState(unitState.state) > getSeverityForState(worstState)
                                        ? unitState.state
                                        : worstState;
                                }, 'Ready');

                                const nextLocationState = {
                                    unitStates: nextUnitStates,
                                    newestTimestampMs,
                                    worstState: recentUnitStates.length > 0
                                        ? worstRecentState
                                        : 'Disconnected',
                                };

                                return { ...prevStates, [locationId]: nextLocationState };
                            });
                        },
                        error: (error) => {
                            console.error("Error in unit state listener:", error);
                        }
                    });
                    unsubscribeCallbacks.push(unsubscribeFirestore)
                }
            }
        }

        return () => {
            for (let i = 0; i < unsubscribeCallbacks.length; i++) {
                unsubscribeCallbacks[i]();
            }
        }
    }, [locations]);

    useEffect(() => {
        const fetchData = async () => {
            if (!userData) {
                setLocations([]);
                setAppCards([]);
                setReviewBacklog({ status: 'idle', total: null, sources: [] });
                return;
            }

            let nextLocations = [];
            if (userData.companies) {
                try {
                    nextLocations = await getAvailableLocations(userData);
                    setLocations(nextLocations);
                } catch (error) {
                    console.error("Error fetching locations: ", error);
                    setLocations([]);
                }
            } else {
                setLocations([]);
            }

            let nextAppCards = [];
            try {
                nextAppCards = await loadAppCards(userData);
                setAppCards(nextAppCards);
            } catch (error) {
                console.error("Error fetching app cards: ", error);
                setAppCards([]);
            }

            await loadReviewBacklog(nextAppCards, nextLocations);
        };

        fetchData();
    }, [userData, setLocations, getAvailableLocations]);

    return (
        <div className="HomePage">
            <div className="wrapper">
                {reviewBacklog.status !== 'idle' && (
                    <div className={`review-backlog-banner review-backlog-${reviewBacklog.status}`}>
                        <div className="review-backlog-title">
                            Ubesvarede anmodninger:{' '}
                            {reviewBacklog.status === 'loading'
                                ? 'Henter...'
                                : reviewBacklog.status === 'error'
                                  ? 'Kunne ikke opgøres'
                                  : reviewBacklog.total}
                        </div>
                        {(reviewBacklog.status === 'ready' || reviewBacklog.status === 'error') && reviewBacklog.sources.length > 0 && (
                            <div className="review-backlog-sources">
                                {reviewBacklog.sources.map((source) => (
                                    <div key={source.key} className="review-backlog-source">
                                        <span>{source.label}</span>
                                        <strong>{source.count}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {userData?.isAdmin && <SignupLinkPanels />}
                {appCards.map((appCard) => (
                    <div key={`${appCard.ownerType}-${appCard.ownerId}`} className="card-wrapper" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <div
                            className="login-container card-container"
                            style={{ width: '100%', maxWidth: '620px', cursor: 'pointer' }}
                            onClick={() => navigateToAppCard(appCard)}
                        >
                            <div className="card-header">
                                <div className="card-header-title">
                                    {appCard.title}
                                </div>
                            </div>
                            <div className="card-body" style={{ marginBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                                <div className="app-card-poster">
                                    App
                                </div>
                            </div>
                            <div className="card-body location-bar" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                                <div className="location-bar-left">
                                    <span className="units-count-text">
                                        <div className="units-count-label">Enheder:</div>
                                        <div className="units-count-number">1</div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {locations.map((location, index) => (
                    <div key={index} className="card-wrapper" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <div 
                            className="login-container card-container" 
                            style={{ width: '100%', maxWidth: '620px', cursor: 'pointer' }}
                            onClick={() => {
                            		navigateToLocation(location.id)
                            	}
                            }
                        >
                            <div className="card-header">
                                <div className="card-header-title">
                                    {location.name}
                                </div>
                            </div>
                            <div className="card-body" style={{ marginBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                                <figure className="img-wrapper">
                                    <img 
                                        className="poster-image"
                                        style={getUnitsCount(location) > 0 ? {} : { filter: 'grayscale(100%)' }}
                                        src={location.poster_url}
                                        width="588"
                                        height="250"
                                        alt="Poster"
                                    />
                                </figure>
                            </div>
                            <div className="card-body location-bar" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                                <div className="location-bar-left">
                                    <span className="units-count-text">
                                        <div className="units-count-label">Enheder:</div>
                                        <div className="units-count-number">{getUnitsCount(location)}</div>
                                    </span>
                                </div>
                                <div className="location-bar-right">
                                    <div className="live-activity-wrapper">
                                        <div className="live-activity-dot" style={{ backgroundColor: getColorForLocation(location.id) }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HomePage;
